/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Rich Markdown, Code Highlighter & Math Formula Renderer (Task 10.3.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { lowlight } from '../editor/extensions/lowlight-config';
import { useI18n } from '../../hooks/useI18n';
import {
  Check,
  Copy,
  Download,
  Code2,
  Sigma,
} from 'lucide-react';

interface MarkdownRendererProps {
  content: string;
  isStreaming?: boolean;
  className?: string;
}

interface ParsedBlock {
  type: 'code' | 'math' | 'table' | 'paragraph' | 'heading' | 'blockquote' | 'list';
  lang?: string;
  content: string;
  level?: number;
  items?: string[];
  tableHeaders?: string[];
  tableRows?: string[][];
}

/**
 * Maps common code block language names to file extensions for download.
 */
function getExtensionForLanguage(lang: string): string {
  const map: Record<string, string> = {
    javascript: 'js',
    js: 'js',
    typescript: 'ts',
    ts: 'ts',
    python: 'py',
    py: 'py',
    html: 'html',
    css: 'css',
    json: 'json',
    bash: 'sh',
    shell: 'sh',
    sql: 'sql',
    rust: 'rs',
    go: 'go',
    php: 'php',
  };
  return map[lang.toLowerCase()] || 'txt';
}

/**
 * Downloads a code snippet as a file to the user's computer.
 */
function downloadCodeFile(code: string, lang: string) {
  const ext = getExtensionForLanguage(lang);
  const blob = new Blob([code], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `code_snippet.${ext}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * 1. Code Block Component with lowlight syntax highlighting, Copy & Download
 */
export const CodeBlockView: React.FC<{ code: string; language?: string }> = ({
  code,
  language = 'typescript',
}) => {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const cleanLang = (language || 'code').trim().toLowerCase();

  const handleCopy = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // Ignored
    }
  };

  // Syntax highlighting using lowlight
  let highlightedHtml = '';
  try {
    const langToUse = lowlight.registered(cleanLang) ? cleanLang : 'typescript';
    const tree = lowlight.highlight(langToUse, code);
    // Serialize hast tree nodes to string
    highlightedHtml = serializeHast(tree);
  } catch {
    highlightedHtml = escapeHtml(code);
  }

  return (
    <div className="my-3 overflow-hidden rounded-xl border border-surface-border bg-surface-elevated text-foreground shadow-md">
      {/* Code Header Bar */}
      <div className="flex items-center justify-between px-3.5 py-2 border-b border-surface-border/60 bg-muted/40 text-xs text-foreground-muted">
        <div className="flex items-center gap-2">
          <Code2 className="w-3.5 h-3.5 text-primary" />
          <span className="font-mono text-[11px] uppercase tracking-wider font-semibold text-foreground">
            {cleanLang}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={handleCopy}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] hover:text-foreground hover:bg-muted transition-colors"
            title={t('common.copyCode')}
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-emerald-500 font-medium">{t('common.copied')}</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                <span>{t('common.copy')}</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => downloadCodeFile(code, cleanLang)}
            className="p-1 rounded-md hover:text-foreground hover:bg-muted transition-colors"
            title={t('common.downloadCode')}
          >
            <Download className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Code Body */}
      <div className="p-3.5 overflow-x-auto text-xs font-mono leading-relaxed scrollbar-thin">
        <pre>
          <code
            dangerouslySetInnerHTML={{ __html: highlightedHtml || escapeHtml(code) }}
          />
        </pre>
      </div>
    </div>
  );
};

/**
 * 2. LaTeX Formula Display Block
 */
export const MathFormulaBlock: React.FC<{ formula: string }> = ({ formula }) => {
  const { t } = useI18n();
  return (
    <div className="my-3 overflow-x-auto rounded-xl border border-primary/20 bg-primary/5 p-3.5 text-center text-foreground font-mono text-xs shadow-sm">
      <div className="flex items-center justify-center gap-2 text-primary font-semibold mb-1 text-[11px]">
        <Sigma className="w-3.5 h-3.5" />
        <span>{t('chat.mathFormulaHeading')}</span>
      </div>
      <div className="py-1 tracking-wide text-foreground font-medium text-sm">
        {formula}
      </div>
    </div>
  );
};

/**
 * 3. Responsive Markdown Table View
 */
export const MarkdownTableView: React.FC<{
  headers: string[];
  rows: string[][];
}> = ({ headers, rows }) => {
  return (
    <div className="my-3 overflow-x-auto rounded-xl border border-surface-border shadow-sm">
      <table className="w-full text-left text-xs border-collapse">
        <thead className="bg-surface-elevated border-b border-surface-border text-foreground font-semibold">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="px-3.5 py-2.5 whitespace-nowrap">
                {renderInlineFormatting(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-border/60 bg-surface">
          {rows.map((row, rIdx) => (
            <tr key={rIdx} className="hover:bg-muted/40 transition-colors">
              {row.map((cell, cIdx) => (
                <td key={cIdx} className="px-3.5 py-2 text-foreground-muted">
                  {renderInlineFormatting(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Parses raw Markdown text into structured blocks (Code, Math, Table, Headings, Lists, Paragraphs).
 */
export function parseMarkdownBlocks(text: string): ParsedBlock[] {
  const blocks: ParsedBlock[] = [];
  const lines = text.split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // 1. Code Block (```lang)
    if (line.trim().startsWith('```')) {
      const lang = line.trim().slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      blocks.push({
        type: 'code',
        lang,
        content: codeLines.join('\n'),
      });
      i++;
      continue;
    }

    // 2. Block Math ($$...$$)
    if (line.trim().startsWith('$$')) {
      const mathLines: string[] = [];
      if (line.trim().length > 2 && line.trim().endsWith('$$') && line.trim() !== '$$') {
        // Single line $$formula$$
        blocks.push({
          type: 'math',
          content: line.trim().slice(2, -2).trim(),
        });
        i++;
        continue;
      }
      i++;
      while (i < lines.length && !lines[i].trim().endsWith('$$')) {
        mathLines.push(lines[i]);
        i++;
      }
      blocks.push({
        type: 'math',
        content: mathLines.join('\n'),
      });
      i++;
      continue;
    }

    // 3. Markdown Table (| Col 1 | Col 2 |)
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const headers = line
        .split('|')
        .slice(1, -1)
        .map((s) => s.trim());
      i++;
      // Check for separator line (e.g. |--|--|)
      if (i < lines.length && lines[i].includes('---')) {
        i++;
      }
      const tableRows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        const row = lines[i]
          .split('|')
          .slice(1, -1)
          .map((s) => s.trim());
        tableRows.push(row);
        i++;
      }
      blocks.push({
        type: 'table',
        content: '',
        tableHeaders: headers,
        tableRows,
      });
      continue;
    }

    // 4. Headings (#, ##, ###)
    if (line.startsWith('#')) {
      const match = line.match(/^(#{1,6})\s+(.*)$/);
      if (match) {
        blocks.push({
          type: 'heading',
          level: match[1].length,
          content: match[2],
        });
        i++;
        continue;
      }
    }

    // 5. Blockquote (> ...)
    if (line.startsWith('>')) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].startsWith('>')) {
        quoteLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      blocks.push({
        type: 'blockquote',
        content: quoteLines.join(' '),
      });
      continue;
    }

    // 6. Bullet or Numbered List
    if (/^\s*([*\-+]|\d+\.)\s+/.test(line)) {
      const listItems: string[] = [];
      while (i < lines.length && /^\s*([*\-+]|\d+\.)\s+/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\s*([*\-+]|\d+\.)\s+/, ''));
        i++;
      }
      blocks.push({
        type: 'list',
        content: '',
        items: listItems,
      });
      continue;
    }

    // 7. Regular paragraph
    if (line.trim()) {
      blocks.push({
        type: 'paragraph',
        content: line,
      });
    }

    i++;
  }

  return blocks;
}

/**
 * Formats inline elements: Bold, Italic, Code, Math, Links.
 */
export function renderInlineFormatting(text: string): React.ReactNode {
  // Simple token regex for inline code, bold, italic, inline math, links
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let keyIdx = 0;

  while (remaining.length > 0) {
    // Inline code `code`
    const codeMatch = remaining.match(/^`([^`]+)`/);
    if (codeMatch) {
      parts.push(
        <code
          key={`code_${keyIdx++}`}
          className="px-1.5 py-0.5 rounded-md bg-muted font-mono text-[11px] text-primary font-medium border border-surface-border"
        >
          {codeMatch[1]}
        </code>
      );
      remaining = remaining.slice(codeMatch[0].length);
      continue;
    }

    // Inline math $formula$
    const mathMatch = remaining.match(/^\$([^$\n]+)\$/);
    if (mathMatch) {
      parts.push(
        <span
          key={`math_${keyIdx++}`}
          className="px-1 py-0.2 rounded bg-primary/10 text-primary font-mono text-[11px] font-semibold"
        >
          {mathMatch[1]}
        </span>
      );
      remaining = remaining.slice(mathMatch[0].length);
      continue;
    }

    // Bold **text**
    const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
    if (boldMatch) {
      parts.push(
        <strong key={`bold_${keyIdx++}`} className="font-bold text-foreground">
          {boldMatch[1]}
        </strong>
      );
      remaining = remaining.slice(boldMatch[0].length);
      continue;
    }

    // Italic *text*
    const italicMatch = remaining.match(/^\*([^*]+)\*/);
    if (italicMatch) {
      parts.push(
        <em key={`italic_${keyIdx++}`} className="italic">
          {italicMatch[1]}
        </em>
      );
      remaining = remaining.slice(italicMatch[0].length);
      continue;
    }

    // Links [title](url)
    const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      parts.push(
        <a
          key={`link_${keyIdx++}`}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:text-accent font-medium transition-colors"
        >
          {linkMatch[1]}
        </a>
      );
      remaining = remaining.slice(linkMatch[0].length);
      continue;
    }

    // Plain text character
    parts.push(remaining[0]);
    remaining = remaining.slice(1);
  }

  return parts;
}

/**
 * Main MarkdownRenderer Component
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  isStreaming = false,
  className = '',
}) => {
  const { t } = useI18n();
  const blocks = parseMarkdownBlocks(content);

  return (
    <div className={`space-y-2 text-xs leading-relaxed text-foreground select-text ${className}`}>
      {blocks.map((block, idx) => {
        switch (block.type) {
          case 'code':
            return (
              <CodeBlockView
                key={idx}
                code={block.content}
                language={block.lang}
              />
            );

          case 'math':
            return <MathFormulaBlock key={idx} formula={block.content} />;

          case 'table':
            return (
              <MarkdownTableView
                key={idx}
                headers={block.tableHeaders || []}
                rows={block.tableRows || []}
              />
            );

          case 'heading': {
            const Tag = `h${block.level || 3}` as keyof JSX.IntrinsicElements;
            const headingClasses: Record<number, string> = {
              1: 'text-lg font-extrabold text-foreground mt-4 mb-2 tracking-tight',
              2: 'text-base font-bold text-foreground mt-3 mb-1.5',
              3: 'text-sm font-bold text-foreground mt-2 mb-1',
              4: 'text-xs font-bold text-foreground mt-1 mb-0.5',
              5: 'text-xs font-semibold text-foreground',
              6: 'text-xs font-medium text-foreground-muted',
            };
            return (
              <Tag key={idx} className={headingClasses[block.level || 3]}>
                {renderInlineFormatting(block.content)}
              </Tag>
            );
          }

          case 'blockquote':
            return (
              <blockquote
                key={idx}
                className="my-2 pl-3 border-l-2 border-primary/50 italic text-foreground-muted bg-surface-elevated/40 py-1 rounded-r-lg"
              >
                {renderInlineFormatting(block.content)}
              </blockquote>
            );

          case 'list':
            return (
              <ul key={idx} className="my-1.5 space-y-1 pl-4 list-disc text-foreground-muted">
                {block.items?.map((item, itemIdx) => (
                  <li key={itemIdx} className="leading-relaxed">
                    {renderInlineFormatting(item)}
                  </li>
                ))}
              </ul>
            );

          case 'paragraph':
          default:
            return (
              <p key={idx} className="leading-relaxed">
                {renderInlineFormatting(block.content)}
              </p>
            );
        }
      })}

      {/* Blinking Cursor during live token streaming */}
      {isStreaming && (
        <span
          className="inline-block w-1.5 h-3.5 bg-primary ml-1 rounded-sm animate-pulse align-middle"
          title={t('chat.streamingData')}
        />
      )}
    </div>
  );
};

// Utilities for lowlight hast serialization
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  properties?: { className?: string[] };
  children?: HastNode[];
}

function serializeHast(node: unknown): string {
  if (!node || typeof node !== 'object') return '';
  const n = node as HastNode;
  if (n.type === 'text') {
    return escapeHtml(n.value || '');
  }
  if (n.children) {
    const inner = n.children.map(serializeHast).join('');
    if (n.tagName) {
      const cls = n.properties?.className?.join(' ') || '';
      return `<${n.tagName}${cls ? ` class="${cls}"` : ''}>${inner}</${n.tagName}>`;
    }
    return inner;
  }
  return '';
}
